import type { CopilotStatefulMessage } from '@customerio/hydra-copilot';

export type GroupedUserMessage = {
  role: 'user';
  type: 'grouped';
  text?: string;
  contentMessages: CopilotStatefulMessage[];
  // Preserve other properties from the base message
  [key: string]: unknown;
};

export type ProcessedMessage = CopilotStatefulMessage | GroupedUserMessage;

/**
 * Group consecutive user messages (text and content) into a single message bubble.
 * This improves the visual display by combining text and file uploads that were sent together.
 */
export function groupUserMessages(messages: CopilotStatefulMessage[]): ProcessedMessage[] {
  const groupedMessages: ProcessedMessage[] = [];
  let i = 0;

  while (i < messages.length) {
    const message = messages[i];

    if (message.role === 'user') {
      // Collect all consecutive user messages
      const userMessages: CopilotStatefulMessage[] = [message];
      let j = i + 1;

      while (j < messages.length && messages[j].role === 'user') {
        userMessages.push(messages[j]);
        j++;
      }

      // If we have multiple user messages, group them
      if (userMessages.length > 1) {
        const textMessage = userMessages.find((m) => m.type === 'text');
        const contentMessages = userMessages.filter((m) => m.type === 'content');

        // Create a grouped message with explicit type
        const groupedMessage: GroupedUserMessage = {
          ...(textMessage ?? contentMessages[0]), // Preserve base message properties
          role: 'user',
          type: 'grouped',
          text: textMessage != null && 'text' in textMessage ? textMessage.text : undefined,
          contentMessages,
        };

        groupedMessages.push(groupedMessage);
      } else {
        // Single user message, add as-is
        groupedMessages.push(message);
      }

      i = j; // Skip the messages we've already processed
    } else {
      // Non-user message, add as-is
      groupedMessages.push(message);
      i++;
    }
  }

  return groupedMessages;
}
